<template>
  <section class="endpoint-page">
    <div class="page-header">
      <p class="eyebrow">Phase 2 Tooling</p>
      <h2>Material Search</h2>
      <p>
        Exercise the generic Phase 2 material search endpoint. Use this to
        inspect catalog availability and filter behavior before generating
        verified candidates.
      </p>
    </div>

    <Card>
      <template #title>Generic Material Search</template>
      <template #content>
        <div class="form-grid">
          <label>
            Equipment Type
            <Dropdown v-model="equipmentType" :options="equipmentTypes" />
          </label>
          <label>
            Search Text
            <InputText
              v-model="searchText"
              placeholder="part/model/manufacturer"
            />
          </label>
          <label>
            Limit
            <InputNumber
              v-model="materialLimit"
              :min="1"
              :max="100"
              show-buttons
            />
            <small>Material search supports up to 100 rows per request.</small>
          </label>
          <label class="wide">
            Filters JSON
            <textarea v-model="filtersJson" rows="8" />
            <small>
              Common filters include `manufacturers`, `flowRateBpd`,
              `maxOuterDiameterIn`, `minHp`, `voltage`, `temperatureF`,
              `minKva`, and `minAmps`.
            </small>
          </label>
        </div>
        <Button
          label="Search Materials"
          class="p-button-success mt-3"
          :loading="isSearching"
          @click="runMaterialSearch"
        />
      </template>
    </Card>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <Card v-if="materialResult" class="mt-4">
      <template #title>Material Search Results</template>
      <template #subtitle>
        {{ materialResult.items.length }} of
        {{ materialResult.totalCount }} results
      </template>
      <template #content>
        <DataTable :value="materialResult.items" responsive-layout="scroll">
          <Column field="equipmentType" header="Type" />
          <Column field="label" header="Label" />
          <Column field="manufacturer" header="Manufacturer" />
          <Column field="partNumber" header="Part Number" />
          <Column field="source" header="Source" />
        </DataTable>
      </template>
    </Card>

    <Panel v-if="materialResult" header="Raw Material JSON" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy Material JSON"
          class="p-button-text"
          :disabled="!materialResult"
          @click="copyJson(materialResult)"
        />
      </div>
      <AdminJsonViewer :value="materialResult" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useToast } from "primevue/usetoast";

import {
  searchMaterials,
  type MaterialSearchResponse
} from "@/libs/aiDos/adminApi";
import {
  copyJsonToClipboard,
  errorMessage,
  MATERIAL_SEARCH_EQUIPMENT_TYPES
} from "@/libs/aiDos/adminShared";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const equipmentTypes = [...MATERIAL_SEARCH_EQUIPMENT_TYPES];
const equipmentType = ref("pump");
const searchText = ref("");
const materialLimit = ref(10);
const filtersJson = ref(JSON.stringify({ manufacturers: ["Summit"] }, null, 2));
const isSearching = ref(false);
const error = ref<string | null>(null);
const materialResult = ref<MaterialSearchResponse | null>(null);

const parseFilters = () => {
  try {
    return filtersJson.value.trim() ? JSON.parse(filtersJson.value) : {};
  } catch {
    throw new Error("Filters JSON is invalid.");
  }
};

const runMaterialSearch = async () => {
  isSearching.value = true;
  error.value = null;
  try {
    materialResult.value = await searchMaterials({
      equipmentType: equipmentType.value,
      search: searchText.value || undefined,
      filters: parseFilters(),
      limit: materialLimit.value || 10
    });
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    isSearching.value = false;
  }
};

const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);
</script>

<style scoped lang="scss">
.page-header {
  max-width: 58rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;

  small {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
    font-weight: 400;
    line-height: 1.35;
  }
}

textarea {
  background: var(--surface-0);
  border: 1px solid var(--surface-400);
  border-radius: 0.375rem;
  color: var(--text-color);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  padding: 0.75rem;
}

.wide {
  grid-column: 1 / -1;
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 1rem;
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.json-output {
  background: #111827;
  border: 1px solid #374151;
  border-radius: 0.5rem;
  color: #f9fafb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 28rem;
  overflow: auto;
  padding: 1rem;
  white-space: pre;
}
</style>
